import { useSearchParams } from 'react-router-dom';
import { RestaurantTitleSuffix } from '@/components/restaurant/RestaurantTitleSuffix';
import TablesTab from '@/components/floor-management/TablesTab';
import FloorsZonesTab from '@/components/floor-management/FloorsZonesTab';
import TableStatusesTab from '@/components/floor-management/TableStatusesTab';
import { useRestaurantId } from '@/hooks/useRestaurantId';
import { DraftModeBar } from '@/components/common/DraftModeBar';
import { PERM } from '@/constants/permissions';
import { AlertTriangle } from 'lucide-react';

const TABS = [
  { id: 'tables', label: 'Live Tables' },
  { id: 'floors', label: 'Floors & Zones' },
  { id: 'statuses', label: 'Table Statuses' },
] as const;

type TabId = typeof TABS[number]['id'];

function isValidTab(value: string | null): value is TabId {
  return value === 'tables' || value === 'floors' || value === 'statuses';
}

export default function FloorManagementPage() {
  const restaurantId = useRestaurantId();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isValidTab(tabParam) ? tabParam : 'tables';

  const setActiveTab = (tab: TabId) => setSearchParams({ tab }, { replace: true });

  if (restaurantId === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Invalid restaurant ID</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Floor Management
            <RestaurantTitleSuffix restaurantId={restaurantId} />
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage floors, tables, zones, and layouts
          </p>
        </div>
      </div>

      {/* Draft mode applies to the floor plan itself. The Live Tables tab shows
          runtime table status flowing up from the POS, which is not a dashboard
          edit and so is never staged. */}
      {activeTab === 'floors' && (
        <DraftModeBar
          restaurantId={restaurantId}
          area="FLOOR"
          permission={PERM.FLOOR_UPDATE}
        />
      )}

      {/* Tab Bar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'tables' && <TablesTab restaurantId={restaurantId} />}
      {activeTab === 'floors' && <FloorsZonesTab restaurantId={restaurantId} />}
      {activeTab === 'statuses' && <TableStatusesTab restaurantId={restaurantId} />}
    </div>
  );
}